import { useState, createElement } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  Container,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import {
  Launch as ExternalIcon,
  Sensors as SensorsIcon,
  Insights as InsightsIcon,
  NotificationsActive as NotificationsIcon,
  FactCheck as FactCheckIcon,
  Shield as ShieldIcon,
  Explore as ExploreIcon,
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import dataHogLogo from '../assets/datahog-logo.png';

// Endurion brand palette (see brand guide). The dark hero banner stays fixed as
// the stable brand surface; the sharp accent color is scheme-driven (see
// `accent` below) so the homepage follows the user's selected color scheme.
// Control Upright TT is the brand display font; until the font file is embedded
// via @font-face the BRAND_FONT stack falls back.
const ENDURION = {
  maroon: '#421212',
  navy: '#1f2132',
  black: '#000000',
  steel: '#aeb7c2',
} as const;
const BRAND_FONT = "'Control Upright TT', Arial, sans-serif";

const DashboardPage = () => {
  const theme = useTheme();
  // Scheme-driven accent: the sharp accent on the splash (title underline, CTA
  // borders/hover, focus rings, pillar hover, chevrons) follows the active
  // color scheme's secondary color. Endurion's secondary is the brand red, so
  // the default look is unchanged; other schemes recolor the accent.
  const accent = theme.palette.secondary.main;
  const [pillarDialogId, setPillarDialogId] = useState<string | null>(null);

  const platformPillars = [
    {
      id: 'ingest',
      title: 'Ingest & Fuse',
      Icon: SensorsIcon,
      description:
        'Unify GPS tracks, RF signals, digital advertising location data, device telemetry, call logs, and browsing history into typed entities with cross-source identity resolution.',
      points: ['Multi-source feeds', 'AdTech location data', 'Identity merge'],
      detail: {
        paragraphs: [
          'Pathfinder ingests GPS track data from device uploads, sensor syncs, and real-time feeds, then normalizes it for downstream analysis. Pathfinder and Pathfinder-Postgres provide high-performance entity lookups and time-range and area-based track queries across large datasets.',
          'Commercial digital advertising (AdTech) location feeds can be pulled alongside organic tracks—by area, registration ID, or IP—so analysts fuse advertising-derived sightings with course devices under one entity view. Cross-source identity resolution keeps a merge trail so provenance is never lost.',
        ],
        bullets: [
          'Hierarchical device trees and course-aware grouping',
          'AdTech / Venntel location ingest with area, ID, and IP lookup',
          'Configurable data retention and route geocoding (TomTom)',
        ],
      },
    },
    {
      id: 'analyze',
      title: 'Analyze & Discover',
      Icon: InsightsIcon,
      description:
        'Surface movement, behavior, and relationships with dwell detection, route extraction, co-traveler mining—including AdTech advertising data—anomaly detection, and embedding-based similarity.',
      points: ['Pattern of Life', 'AdTech co-travelers', 'Scheduled analytics'],
      detail: {
        paragraphs: [
          'The Analysis workflow covers dwell detection, route extraction, co-traveler discovery, temporal pattern mining, and scheduled recurring analyses with Quick-Launch presets. Interactive time-window charts and digital pattern-of-life extraction help teams move from raw sightings to defensible conclusions.',
          'When AdTech is enabled, area search and co-traveler tools can query commercial advertising location data, load full advertising-device pattern-of-life onto the map, and compare those tracks against trainees and organic devices in the same workspace.',
        ],
        bullets: [
          'AI-assisted area and story-mode analysis over large datasets',
          'AdTech area search, IP/ID lookup, and advertising-device PoL',
          'Results views: heatmaps, Gantt charts, routes, WebGL-rendered maps',
        ],
      },
    },
    {
      id: 'monitor',
      title: 'Monitor & Respond',
      Icon: NotificationsIcon,
      description:
        'Run live operations with a real-time Common Operating Picture, geofence entry/exit tracking, SOS acknowledgment, and team chat with channels.',
      points: ['Live dashboards', 'Geofence alerts', 'Team collaboration'],
      detail: {
        paragraphs: [
          'The Common Operating Picture module is built for live operations: device locations and timelines, dwell displays, SOS alert acknowledgment, and a Quick Ask assistant alongside real-time team chat with channels, reactions, and typing indicators.',
          'Geofence and geofence-activity workflows give polygon boundaries with entry/exit tracking and duration analysis so operators can respond to movement in sensitive areas.',
        ],
        bullets: [
          'Annotations and Notes for analyst tagging on any entity',
          'Azure AD single sign-on with role-based access control',
          'Modules can be enabled or detached per deployment or classification',
        ],
      },
    },
    {
      id: 'missions',
      title: 'Mission Explorer',
      Icon: ExploreIcon,
      description:
        'Plan a route, go live with assigned kit, and watch the run — alerts, follows, photos, dwells, areas, and what the scanners heard — in one workspace.',
      points: ['Live COP', 'Route & Sentinel', 'Watchlist & RF'],
      href: '/missions',
      detail: {
        paragraphs: [
          'Mission Explorer is the field workspace. A mission has a planned line, assigned phones, and watched areas. Once it is under way the map and the right-hand dock stay on the same run: live alerts, who is travelling with you, field-report photos, stay-points, and an on-request RF digest.',
          'Sentinel can talk about the board and offer a route around confirmed threats. Own kit is learned before departure so the convoy is not scored as its own follower. GPS jitter is clustered so a parked radio is one place, not several.',
        ],
        bullets: [
          'Plan, rehearse, and go live on the same route',
          'Standing alerts for watchlist and confirmed followers — not a raw packet waterfall',
          'Signals survey on demand; multi-location notices stay a warning, not a map pin',
        ],
      },
    },
    {
      id: 'signals',
      title: 'Signals & Force Protection',
      Icon: ShieldIcon,
      description:
        'Detect and characterize the RF environment with BLE, WiFi, GNSS, TDOA, and Remote ID feeds plus Digital Force Protection alerting with deduplication.',
      points: ['RF anomaly detection', 'DFP alerts', 'OpenCellID reference'],
      detail: {
        paragraphs: [
          'Pathfinder-RF extends the platform into signals intelligence: Digital Force Protection alerting with deduplication, signal waterfalls, BLE/WiFi/GNSS/TDOA detectors, Remote ID drone ingestion, a signal-environment panel, and RF anomaly detection.',
          'Cell-tower reference data via OpenCellID helps analysts ground RF observations in real-world infrastructure context.',
        ],
        bullets: [
          'Dedicated RF-oriented assistants tuned for threat interpretation',
          'Link Analysis draws on dwells, co-travelers, and comms-derived edges for graph context',
          'External customer API for Story Mode timeline export where integrations apply',
        ],
      },
    },
    {
      id: 'reason',
      title: 'Reason & Report',
      Icon: FactCheckIcon,
      description:
        'Ask questions in natural language, search open-source media reporting with OSINT, and export cited briefings to PowerPoint, PDF, CSV, and JSON.',
      points: ['Media reporting', 'Sentinel assistants', 'Briefing exports'],
      detail: {
        paragraphs: [
          'OSINT brings open-source media reporting into the workflow: keyword, map-area, and radius search over geocoded articles (including foreign-language / translated coverage), plot hits on the map, and save selected stories into the Intel Packet alongside telemetry findings.',
          'Sentinel-style assistants span Pathfinder, Pattern of Life, Link Analysis, and dashboards — so analysts can ask operational questions and receive answers with citations. Dashboards and exports cover HTML, PDF, CSV, JSON, and PowerPoint for briefing-ready outputs.',
        ],
        bullets: [
          'OSINT (GDELT media): area/keyword search, map markers, Intel Packet save',
          'Dashboard Builder: saved, shareable layouts with Ask Sentinel',
          'Full workflow from raw telemetry and media to briefing-ready outputs',
        ],
      },
    },
  ];

  const selectedPillar = pillarDialogId
    ? platformPillars.find((p) => p.id === pillarDialogId)
    : null;

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', pb: { xs: 4, sm: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 4, md: 6 },
          mb: { xs: 2, sm: 4 },
          mx: { xs: -1, sm: 0 },
          borderRadius: { xs: 0, sm: 2 },
          background: `linear-gradient(135deg, #2b2f3a 0%, ${ENDURION.navy} 55%, #14151b 100%)`,
          color: 'white',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          borderBottom: `3px solid ${accent}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 50% 35%, rgba(255, 255, 255, 0.06) 0%, transparent 55%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, px: { xs: 1, sm: 2 } }}>
          <Box sx={{ mb: { xs: 2, sm: 4 } }}>
            <Box sx={{ mb: { xs: 2, sm: 3 } }}>
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  color: 'common.white',
                  fontFamily: BRAND_FONT,
                  fontWeight: 800,
                  letterSpacing: { xs: 3, sm: 5 },
                  lineHeight: 1,
                  fontSize: { xs: '2rem', sm: '2.75rem', md: '3.25rem' },
                  textShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
                }}
              >
                PATHFINDER
              </Typography>
              <Box
                sx={{
                  width: 72,
                  height: 4,
                  mx: 'auto',
                  mt: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  backgroundColor: accent,
                  boxShadow: `0 0 16px ${accent}99`,
                }}
              />
            </Box>
            <Typography
              variant="h5"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                opacity: 0.9,
                mb: { xs: 2, sm: 3 },
                fontWeight: 300,
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              Intelligence & Analysis Platform
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} justifyContent="center" sx={{ px: { xs: 2, sm: 0 } }}>
            {(() => {
              // Shared sizing so all three CTA buttons render at the same width
              // regardless of label length. minWidth covers desktop, width:100%
              // makes them stretch evenly when the Stack collapses to a column
              // on mobile.
              const sharedSx = {
                minWidth: { sm: 220 },
                width: { xs: '100%', sm: 'auto' },
                height: 48,
                whiteSpace: 'nowrap' as const,
              };
              return (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={
                      <Box
                        component="img"
                        src={dataHogLogo}
                        alt=""
                        sx={{
                          width: 40,
                          height: 28,
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4))',
                        }}
                      />
                    }
                    onClick={() =>
                      window.open('https://qpjoesandbox-hrcubnb6h7hugnhd.eastus2-01.azurewebsites.net/auth/login', '_blank')
                    }
                    sx={{
                      ...sharedSx,
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      color: 'white',
                      backdropFilter: 'blur(10px)',
                      border: `1px solid ${accent}73`,
                      '&:hover': {
                        backgroundColor: `${accent}26`,
                        borderColor: accent,
                      },
                    }}
                  >
                    DataHog
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      window.location.href = '/results';
                    }}
                    sx={{
                      ...sharedSx,
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.35)',
                      '&:hover': {
                        borderColor: accent,
                        backgroundColor: `${accent}1f`,
                      },
                    }}
                  >
                    View Analysis Results
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<ExternalIcon />}
                    onClick={() => window.open('https://osintexplorer.endurion.com/', '_blank')}
                    sx={{
                      ...sharedSx,
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.35)',
                      '&:hover': {
                        borderColor: accent,
                        backgroundColor: `${accent}1f`,
                      },
                    }}
                  >
                    OSINT Explorer
                  </Button>
                </>
              );
            })()}
          </Stack>
        </Container>
      </Paper>
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Box sx={{ mb: { xs: 4, sm: 6 } }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 } }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: accent,
                    letterSpacing: 2,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                >
                  Capability Summary
                </Typography>
                <Typography
                  variant="h4"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    mt: 0.5,
                    mb: 1,
                    fontSize: { xs: '1.375rem', sm: '1.75rem', md: '2rem' },
                  }}
                >
                  A full analytical workflow, end to end
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ maxWidth: 760, mx: 'auto', lineHeight: 1.6 }}
                >
                  From raw telemetry and digital advertising data to behavioral mining, media
                  reporting, real-time operations, and natural-language reasoning over a typed
                  knowledge graph.
                </Typography>
              </Box>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                {platformPillars.map((pillar) => {
                  const PillarIcon = pillar.Icon;
                  return (
                  <Grid item xs={12} sm={6} md={4} key={pillar.id}>
                    <Paper
                      variant="outlined"
                      role="button"
                      tabIndex={0}
                      aria-label={`${pillar.title}: view details`}
                      onClick={() => setPillarDialogId(pillar.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setPillarDialogId(pillar.id);
                        }
                      }}
                      sx={{
                        height: '100%',
                        p: { xs: 2.5, sm: 3 },
                        borderRadius: 2,
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
                        '&:hover': {
                          borderColor: accent,
                          boxShadow: `0 4px 20px ${accent}26`,
                          transform: 'translateY(-2px)',
                        },
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: accent,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(255, 255, 255, 0.06)',
                          color: ENDURION.steel,
                          mb: 1.75,
                        }}
                      >
                        <PillarIcon sx={{ fontSize: 28 }} />
                      </Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 0.75, fontSize: '1.05rem' }}
                      >
                        {pillar.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.55, mb: 2, flex: 1 }}
                      >
                        {pillar.description}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {pillar.points.map((point) => (
                          <Box
                            key={point}
                            sx={{
                              px: 1.25,
                              py: 0.375,
                              borderRadius: 10,
                              bgcolor: 'action.hover',
                              fontSize: '0.72rem',
                              fontWeight: 500,
                              color: 'text.secondary',
                              letterSpacing: 0.2,
                            }}
                          >
                            {point}
                          </Box>
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.disabled" sx={{ mt: 1.5, display: 'block' }}>
                        Click for details
                      </Typography>
                    </Paper>
                  </Grid>
                  );
                })}
            </Grid>
          </Box>
      </Container>

      <Dialog
        open={Boolean(selectedPillar)}
        onClose={() => setPillarDialogId(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        aria-labelledby="pillar-detail-title"
      >
        {selectedPillar && (
          <>
            <DialogTitle
              id="pillar-detail-title"
              sx={{
                pr: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                pb: 1,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255, 255, 255, 0.06)',
                  color: ENDURION.steel,
                  flexShrink: 0,
                }}
              >
                {createElement(selectedPillar.Icon, { sx: { fontSize: 28 } })}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography component="span" variant="h6" sx={{ fontWeight: 700, display: 'block' }}>
                  {selectedPillar.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.4 }}>
                  {selectedPillar.description}
                </Typography>
              </Box>
              <IconButton
                aria-label="Close"
                onClick={() => setPillarDialogId(null)}
                sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ pt: 2 }}>
              {selectedPillar.detail.paragraphs.map((para, i) => (
                <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.65 }}>
                  {para}
                </Typography>
              ))}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Highlights
              </Typography>
              <List dense disablePadding>
                {selectedPillar.detail.bullets.map((item) => (
                  <ListItem key={item} disableGutters sx={{ alignItems: 'flex-start', py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28, mt: 0.35 }}>
                      <ChevronRightIcon sx={{ fontSize: 18, color: accent }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{ variant: 'body2', color: 'text.primary', sx: { lineHeight: 1.55 } }}
                    />
                  </ListItem>
                ))}
              </List>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
                At a glance
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {selectedPillar.points.map((point) => (
                  <Box
                    key={point}
                    sx={{
                      px: 1.25,
                      py: 0.375,
                      borderRadius: 10,
                      bgcolor: 'action.hover',
                      fontSize: '0.72rem',
                      fontWeight: 500,
                      color: 'text.secondary',
                    }}
                  >
                    {point}
                  </Box>
                ))}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              {selectedPillar.href ? (
                <>
                  <Button onClick={() => setPillarDialogId(null)}>Close</Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      window.location.href = selectedPillar.href as string;
                    }}
                  >
                    Open {selectedPillar.title}
                  </Button>
                </>
              ) : (
                <Button variant="contained" onClick={() => setPillarDialogId(null)}>
                  Close
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default DashboardPage;
